<script setup lang="ts">
import PolicyDetailsForm from '@/components/asicw/policies/create-dialog/PolicyDetailsForm.vue'
import PolicyGroupForm from '@/components/asicw/policies/create-dialog/PolicyGroupForm.vue'
import PolicyMinersForm from '@/components/asicw/policies/create-dialog/PolicyMinersForm.vue'
import RlDialog from '@/components/common/RlDialog.vue'
import { usePolicies } from '@/composables/asicw/policies'
import { CoolingMethod, Environment, type IPolicyGroupCreationForm } from '@/types/asicw/policies'
import { pick } from 'lodash'
import { computed, reactive, ref } from 'vue'

const open = defineModel<boolean>()

const policyGroupFormRef = ref<InstanceType<typeof PolicyGroupForm>>()
const policyDetailsFormRef = ref<InstanceType<typeof PolicyDetailsForm>>()
const policyMinersFormRef = ref<InstanceType<typeof PolicyMinersForm>>()

const step = ref(1)

const canGoNext = computed(
  () =>
    (step.value == 1 && policyGroupFormRef.value?.isFormValid) ||
    (step.value == 2 && policyDetailsFormRef.value?.isFormValid) ||
    (step.value == 3 && policyMinersFormRef.value?.isFormValid)
)

const formInitialState = () =>
  structuredClone({
    group: {
      groupNumber: undefined,
      customer: undefined,
      creationDate: new Date(),
      initialTerm: 12,
      subsequentTerm: 2,
      serviceAgreement: undefined,
    },
    details: {
      location: undefined,
      policySalePrice: 0,
      environment: Environment.ModularDataCenter,
      coolingMethod: CoolingMethod.AirCooled,
      running3rdPartySoft: false,
      overclockingMiners: false,
      numberOfPolicies: undefined,
      addByCSV: false,
      minersCSV: undefined,
    },
    miners: [],
  })

const policyCreationForm = reactive<IPolicyGroupCreationForm>(formInitialState())

/**
 * Produces the initial array of miners for the step 3 reflecting
 * the default values taken from policyCreationForm.details (step 2)
 */
const buildMinersArrayFromPoliciesDetails = () => {
  if (!policyCreationForm.details.numberOfPolicies) return

  if (policyCreationForm.miners.length > policyCreationForm.details.numberOfPolicies) {
    // This actually chops off the array - JS ¯\_(ツ)_/¯
    policyCreationForm.miners.length = policyCreationForm.details.numberOfPolicies
    return
  }

  // Complete with new miners
  for (let i = policyCreationForm.miners.length; i < policyCreationForm.details.numberOfPolicies; i++) {
    policyCreationForm.miners.push({
      ...pick(policyCreationForm.details, [
        'environment',
        'coolingMethod',
        'running3rdPartySoft',
        'overclockingMiners',
      ]),
      sn: '',
      model_family: undefined,
      policySalePrice: 0,
      number: i,
    })
  }
}

const onNext = () => {
  // When going from step 2 to 3, pre-populate `miners` array for step 3 or jump right to POST if we've got a CSV file
  if (step.value == 2)
    if (!policyCreationForm.details.addByCSV) buildMinersArrayFromPoliciesDetails()
    else return postPolicyCreationForm()
  // After step 3, POST
  if (step.value == 3) return postPolicyCreationForm()
  // Step
  step.value += 1
}

const onBack = () => {
  step.value -= 1
  // When going back to step 2, adjust the number of policies to match the number of miners in step 3
  if (step.value == 2) policyCreationForm.details.numberOfPolicies = policyCreationForm.miners.length
}

// Reset the forms
const onClosed = () => {
  step.value = 1
  Object.assign(policyCreationForm, formInitialState())
  policyGroupFormRef.value?.reset()
  policyDetailsFormRef.value?.reset()
  isPending.value = false
}

const { createPolicyGroupMutation } = usePolicies()
const { mutate: postPolicyGroup } = createPolicyGroupMutation
const isPending = ref(false)

const postPolicyCreationForm = () => {
  isPending.value = true
  postPolicyGroup(policyCreationForm, {
    onSuccess: () => {
      open.value = false
    },
    onError: () => {
      isPending.value = false
    },
  })
}
</script>

<template>
  <RlDialog
    v-model="open"
    title="Create Policy Group"
    class="create-policy-dialog"
    :width="544"
    align-center
    @closed="onClosed"
  >
    <!-- Steps -->
    <el-steps class="w-[440px]" :active="step" finish-status="success" align-center>
      <el-step :class="{ active: step == 1 }" :status="step > 1 ? 'success' : 'wait'" title="Policy group" />
      <el-step :class="{ active: step == 2 }" :status="step > 2 ? 'success' : 'wait'" title="Policy details" />
      <el-step
        :class="{ active: step == 3 }"
        :status="step > 3 || policyCreationForm.details.minersCSV ? 'success' : 'wait'"
        title="Miners"
      />
    </el-steps>

    <!-- Forms -->
    <PolicyGroupForm v-if="step == 1" ref="policyGroupFormRef" v-model="policyCreationForm.group" />
    <PolicyDetailsForm v-if="step == 2" ref="policyDetailsFormRef" v-model="policyCreationForm.details" />
    <PolicyMinersForm v-if="step == 3" ref="policyMinersFormRef" v-model="policyCreationForm.miners" />

    <template #footer>
      <el-button v-if="step > 1" size="large" @click="onBack">Back</el-button>
      <el-button size="large" type="primary" :disabled="!canGoNext" :loading="isPending" @click="onNext">{{
        step < 3 ? 'Next' : 'Create'
      }}</el-button>
    </template>
  </RlDialog>
</template>

<!-- <style scoped lang="scss"> -->
<style lang="scss">
// Customize dialog
.create-policy-dialog {
  border: none !important;
  padding: 0;

  // Customize the steps style
  .el-steps {
    padding: 16px 0;
  }

  .el-step {
    // Make the lines between states thin and remove the overlay one
    .el-step__line {
      height: 1px;
      background-color: var(--component-stroke-primary);

      &-inner {
        display: none;
      }
    }

    &__icon-inner {
      font-weight: 300;
      color: var(--text-secondary);
    }

    &__icon.is-text {
      font-weight: 100;
      background-color: var(--component-fill-secondary);
      border: 1px solid var(--component-stroke-primary);
    }

    &__title {
      color: var(--text-tertiary);
      font-weight: 300;
    }

    // Make the head stroke and border brand blue when in success state
    &__head.is-success {
      .el-step__icon.is-text {
        background-color: var(--component-fill-secondary) !important;
        border: 1px solid var(--text-brand);

        path {
          color: var(--text-brand);

          // I found no way of "bolding" it and I've already spent almost an hour on this
          // This is documenting the closest I got
          transform: scale(1.05);
          transform-origin: center;
        }
      }
    }
  }

  // Highlight active step
  .el-step.active {
    .el-step {
      &__icon.is-text {
        background-color: var(--fill-tertiary);
      }

      &__title {
        color: var(--text-primary);
      }
    }
  }

  // Customize the date picker's width
  .el-input.el-date-editor {
    width: 160px;
  }

  // Customize the form's labels
  .el-form-item {
    &__label {
      color: var(--text-tertiary);
      text-align: left;
    }
  }
}
</style>
